import { useCourseStore } from '@/stores/courseStore';
import { usePlanStore } from '@/stores/planStore';
import { validateFractionationInput } from '@/composables/calculationConstants';
import {
  readCalculationSession,
  writeCalculationSession,
} from '@/utils/calculationSessionStorage';
import {
  computeTotalAbsorbedDose,
  normalizeDoseValue,
  normalizeFractionsValue,
} from '@/utils/fractionationDose';

function draftHasAnyFractionation(d) {
  if (!d || typeof d !== 'object') return false;
  const t = d.total_absorbed_dose;
  const dp = d.dose_per_fraction;
  const n = d.number_of_fractions;
  return (
    (t != null && t !== '' && Number.isFinite(Number(t))) ||
    (dp != null && dp !== '' && Number.isFinite(Number(dp))) ||
    (n != null && n !== '' && Number.isFinite(Number(n)))
  );
}

/**
 * GET /calculator/courses/ отдаёт поля курса и агрегаты последнего расчёта (totalDose, n_f).
 */
export function fractionationFromSelectedCourse(course) {
  if (!course || typeof course !== 'object') return null;
  const totalRaw =
    course.total_absorbed_dose != null && course.total_absorbed_dose !== ''
      ? course.total_absorbed_dose
      : course.totalDose;
  const nfRaw =
    course.number_of_fractions != null && course.number_of_fractions !== ''
      ? course.number_of_fractions
      : course.n_f;
  let dpfRaw = course.dose_per_fraction;
  let total = totalRaw != null && totalRaw !== '' ? Number(totalRaw) : null;
  let nf = nfRaw != null && nfRaw !== '' ? Number(nfRaw) : null;
  if (
    (dpfRaw == null || dpfRaw === '') &&
    total != null &&
    Number.isFinite(total) &&
    nf != null &&
    Number.isFinite(nf) &&
    nf > 0
  ) {
    dpfRaw = total / nf;
  }
  let dpf = dpfRaw != null && dpfRaw !== '' ? Number(dpfRaw) : null;
  if (
    (total == null || !Number.isFinite(total)) &&
    dpf != null &&
    Number.isFinite(dpf) &&
    nf != null &&
    Number.isFinite(nf) &&
    nf > 0
  ) {
    total = dpf * nf;
  }
  if (
    (nf == null || !Number.isFinite(nf)) &&
    total != null &&
    Number.isFinite(total) &&
    dpf != null &&
    Number.isFinite(dpf) &&
    dpf > 0
  ) {
    nf = Math.max(1, Math.round(total / dpf));
  }
  if (
    (total == null || !Number.isFinite(total)) &&
    (dpf == null || !Number.isFinite(dpf)) &&
    (nf == null || !Number.isFinite(nf))
  ) {
    return null;
  }
  return {
    total_absorbed_dose: total,
    dose_per_fraction: dpf,
    number_of_fractions: nf,
  };
}

export function buildFractionationFormState() {
  const courseStore = useCourseStore();
  const planStore = usePlanStore();
  const draft = planStore.fractionationDraft || {};
  const draftHasValues = draftHasAnyFractionation(draft);
  if (!draftHasValues) {
    const fromCourse = fractionationFromSelectedCourse(courseStore.selectedCourse);
    if (fromCourse) {
      planStore.setFractionationDraft(fromCourse);
    }
  }
  const d = draftHasValues ? draft : planStore.fractionationDraft || {};
  const dose_per_fraction = normalizeDoseValue(d.dose_per_fraction);
  const number_of_fractions = normalizeFractionsValue(d.number_of_fractions);
  return {
    dose_per_fraction,
    number_of_fractions,
    total_absorbed_dose:
      computeTotalAbsorbedDose(dose_per_fraction, number_of_fractions) ??
      normalizeDoseValue(d.total_absorbed_dose),
  };
}

function syncSelectedCourseFractionation(fractionation) {
  const courseStore = useCourseStore();
  const course = courseStore.selectedCourse;
  if (!course || !fractionation) return;
  courseStore.selectedCourse = {
    ...course,
    total_absorbed_dose: fractionation.total_absorbed_dose,
    dose_per_fraction: fractionation.dose_per_fraction,
    number_of_fractions: fractionation.number_of_fractions,
    totalDose: fractionation.total_absorbed_dose,
    n_f: fractionation.number_of_fractions,
  };
}

function rememberFractionationOnSession(fractionation) {
  const session = readCalculationSession();
  if (!session?.mode || session.openMode) return;
  writeCalculationSession({ ...session, fractionation: { ...fractionation } });
}

function normalizedKeptFractionation(fractionation) {
  const check = validateFractionationInput(fractionation);
  if (!check.ok) return null;
  const storedTotal = Number(fractionation?.total_absorbed_dose);
  return {
    total_absorbed_dose: Number.isFinite(storedTotal) && storedTotal > 0
      ? storedTotal
      : check.total_absorbed_dose,
    dose_per_fraction: check.dose_per_fraction,
    number_of_fractions: check.number_of_fractions,
  };
}

/**
 * D/d/n_f, которые нельзя затирать параметрами последнего прогона.
 * Явная история (openMode) всегда берёт дозу записи.
 * @param {object|null|undefined} payload
 * @param {{ fractionationDraft?: object, fractionationDraftPinned?: boolean }|null|undefined} planStore
 */
export function fractionationToKeepOnRestore(payload, planStore) {
  if (payload?.openMode) return null;
  if (planStore?.fractionationDraftPinned) {
    const pinned = normalizedKeptFractionation(planStore.fractionationDraft);
    if (pinned) return pinned;
  }
  return normalizedKeptFractionation(payload?.fractionation);
}

export function persistFractionationDraft(form) {
  const dose_per_fraction = normalizeDoseValue(form.dose_per_fraction);
  const number_of_fractions = normalizeFractionsValue(form.number_of_fractions);
  const fractionation = {
    dose_per_fraction,
    number_of_fractions,
    total_absorbed_dose: computeTotalAbsorbedDose(dose_per_fraction, number_of_fractions),
  };
  const planStore = usePlanStore();
  planStore.setFractionationDraft(fractionation);
  if (planStore.selectedPlanId) {
    planStore.attachDraftToPlan(planStore.selectedPlanId);
  }
  if (!validateFractionationInput(fractionation).ok) {
    planStore.fractionationDraftPinned = false;
    return;
  }
  planStore.fractionationDraftPinned = true;
  syncSelectedCourseFractionation(fractionation);
  rememberFractionationOnSession(fractionation);
}
